import type {
  DeliveryCandidateArtifactRow,
  DeliveryCandidateRow,
  RuntimeObservationRow
} from '../api';

export type ContainerPlannerKind = 'api' | 'web';

export type CurrentContainerSource = {
  candidate: DeliveryCandidateRow;
  artifact: DeliveryCandidateArtifactRow;
};

export function containerPlannerKind(
  artifact: DeliveryCandidateArtifactRow
): ContainerPlannerKind | undefined {
  const identity =
    `${artifact.key} ${artifact.label} ${artifact.repository || ''}`.toLowerCase();
  const api = /(^|[^a-z])(api|backend|server|service|services)([^a-z]|$)/.test(
    identity
  );
  const web = /(^|[^a-z])(admin|client|front|frontend|web)([^a-z]|$)/.test(
    identity
  );
  if (api === web) return undefined;
  return api ? 'api' : 'web';
}

export function containerMatchesPlannerKind(
  artifact: DeliveryCandidateArtifactRow,
  kind: ContainerPlannerKind
) {
  return containerPlannerKind(artifact) === kind;
}

function candidateTime(candidate: DeliveryCandidateRow) {
  return new Date(
    candidate.deployedAt || candidate.updated || candidate.created || 0
  ).getTime();
}

function releaseUnitKey(
  candidate: DeliveryCandidateRow,
  artifact: DeliveryCandidateArtifactRow
) {
  const projectScope =
    candidate.projectId ||
    [...candidate.projectIds].sort().join(',') ||
    candidate.externalRepo;
  const unit =
    containerPlannerKind(artifact) ||
    artifact.key.trim().toLowerCase() ||
    artifact.label.trim().toLowerCase();
  return `${candidate.environment.trim().toLowerCase()}::${projectScope}::${unit}`;
}

/**
 * Returns the newest deployed record for each logical release unit.
 * Delivery candidates are an audit log. Showing every candidate as current state
 * makes old deployments look live and can send an operator to the wrong build.
 */
export function selectCurrentContainerSources(
  candidates: DeliveryCandidateRow[]
): CurrentContainerSource[] {
  const seen = new Set<string>();
  return [...candidates]
    .filter((candidate) => candidate.status === 'deployed')
    .sort((left, right) => candidateTime(right) - candidateTime(left))
    .flatMap((candidate) =>
      candidate.artifacts
        .filter(
          (artifact) =>
            artifact.kind === 'container' &&
            Boolean(artifact.digest || artifact.tag || artifact.artifactId)
        )
        .map((artifact) => ({ candidate, artifact }))
    )
    .filter(({ candidate, artifact }) => {
      const key = releaseUnitKey(candidate, artifact);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function summarizeRuntime(
  workloads: string[],
  candidate: DeliveryCandidateRow,
  observations: RuntimeObservationRow[]
) {
  if (!workloads.length)
    return {
      runtimeStatus: 'Workloads not mapped',
      runtimeTone: 'unknown' as const
    };
  const projectIds = new Set(
    [candidate.projectId, ...candidate.projectIds].filter(Boolean)
  );
  const relevant = observations.filter(
    (item) =>
      projectIds.has(item.projectId) &&
      item.environment === candidate.environment
  );
  const ambiguousWorkloads: Array<{
    name: string;
    matches: RuntimeObservationRow[];
  }> = [];
  const matched = workloads.map((declared) => {
    const name = declared.split('/').pop() || declared;
    const matches = relevant.filter((item) => item.workload === name);
    if (matches.length > 1) ambiguousWorkloads.push({ name, matches });
    return matches.length === 1 ? matches[0] : undefined;
  });
  if (ambiguousWorkloads.length) {
    const details = ambiguousWorkloads.slice(0, 2).map(({ name, matches }) => {
      const sources = matches
        .slice(0, 2)
        .map(
          (item) => `${item.namespace} ${item.workloadKind} (${item.status})`
        )
        .join(', ');
      return `${name}: ${sources}${matches.length > 2 ? `, +${matches.length - 2} more` : ''}`;
    });
    if (ambiguousWorkloads.length > 2)
      details.push(`+${ambiguousWorkloads.length - 2} more workloads`);
    return {
      runtimeStatus: 'More than one workload matched',
      runtimeDetail: details.join('; '),
      runtimeTone: 'unknown' as const
    };
  }
  const seen = matched.filter((item): item is RuntimeObservationRow =>
    Boolean(item)
  );
  if (seen.length !== workloads.length)
    return {
      runtimeStatus: `${workloads.length - seen.length} workload${workloads.length - seen.length === 1 ? '' : 's'} unseen`,
      runtimeTone: 'unknown' as const
    };
  if (
    seen.some((item) => item.freshness === 'stale' || item.status === 'unknown')
  )
    return {
      runtimeStatus: 'Observation stale',
      runtimeTone: 'unknown' as const
    };
  const ready = seen.filter((item) => item.status === 'ready').length;
  if (seen.some((item) => item.status === 'degraded'))
    return {
      runtimeStatus: `Degraded · ${ready}/${seen.length} ready`,
      runtimeTone: 'failed' as const
    };
  if (seen.some((item) => item.status === 'progressing'))
    return {
      runtimeStatus: `Rolling out · ${ready}/${seen.length} ready`,
      runtimeTone: 'deploying' as const
    };
  return {
    runtimeStatus: `${ready}/${seen.length} workloads ready`,
    runtimeTone: 'deployed' as const
  };
}

type RuntimeImageSummary = {
  imageStatus: string;
  imageDetail?: string;
  imageTone: 'deployed' | 'failed' | 'unknown';
};

function normalizedRepository(reference: string) {
  const withoutScheme = reference
    .replace(/^(?:docker-pullable|containerd):\/\//, '')
    .trim()
    .toLowerCase();
  const withoutDigest = withoutScheme.split('@', 1)[0];
  const slash = withoutDigest.lastIndexOf('/');
  const colon = withoutDigest.lastIndexOf(':');
  return colon > slash ? withoutDigest.slice(0, colon) : withoutDigest;
}

function observedDigest(reference: string) {
  return /@sha256:([a-f0-9]{64})(?:$|[^a-f0-9])/i
    .exec(reference)?.[1]
    ?.toLowerCase();
}

function imageReferences(
  container: NonNullable<RuntimeObservationRow['containers']>[number]
) {
  return [
    ...(container.images || []),
    ...(container.image ? [container.image] : []),
    ...(container.imageIds || []),
    ...(container.imageId ? [container.imageId] : [])
  ];
}

/**
 * Compares the selected immutable artifact with image identities observed in every mapped workload.
 * Missing, stale, ambiguous or insufficient evidence stays unknown; workload readiness is separate.
 */
export function summarizeRuntimeImage(
  workloads: string[],
  candidate: DeliveryCandidateRow,
  artifact: DeliveryCandidateArtifactRow,
  observations: RuntimeObservationRow[]
): RuntimeImageSummary {
  const expectedDigest = /^sha256:([a-f0-9]{64})$/i
    .exec(artifact.digest || '')?.[1]
    ?.toLowerCase();
  const repository = artifact.repository
    ? normalizedRepository(artifact.repository)
    : '';
  if (!expectedDigest || !repository)
    return {
      imageStatus: 'Image not verified',
      imageDetail:
        'The selected build has no valid repository and SHA-256 digest.',
      imageTone: 'unknown'
    };
  if (!workloads.length)
    return {
      imageStatus: 'Image not verified',
      imageDetail: 'No Kubernetes workloads are mapped to this build.',
      imageTone: 'unknown'
    };

  const projectIds = new Set(
    [candidate.projectId, ...candidate.projectIds].filter(Boolean)
  );
  const relevant = observations.filter(
    (item) =>
      projectIds.has(item.projectId) &&
      item.environment === candidate.environment
  );
  const checked: string[] = [];
  const mismatches: string[] = [];

  for (const declared of workloads) {
    const name = declared.split('/').pop() || declared;
    const matches = relevant.filter((item) => item.workload === name);
    if (
      matches.length !== 1 ||
      matches[0].freshness !== 'fresh' ||
      matches[0].status === 'unknown'
    ) {
      return {
        imageStatus: 'Image not verified',
        imageDetail: `${name}: workload evidence is missing, stale or ambiguous.`,
        imageTone: 'unknown'
      };
    }
    const observation = matches[0];
    const containers = observation.containers?.length
      ? observation.containers
      : observation.image || observation.imageId
        ? [
            {
              name,
              image: observation.image,
              imageId: observation.imageId,
              restarts: observation.restarts || 0
            }
          ]
        : [];
    const matchingContainers = containers.filter((container) =>
      imageReferences(container).some(
        (reference) => normalizedRepository(reference) === repository
      )
    );
    if (!matchingContainers.length) {
      return {
        imageStatus: 'Image not verified',
        imageDetail: `${name}: no observed container image matches ${artifact.repository}.`,
        imageTone: 'unknown'
      };
    }
    for (const container of matchingContainers) {
      if (container.imageEvidenceComplete === false) {
        return {
          imageStatus: 'Image not verified',
          imageDetail: `${name}/${container.name}: more than 100 distinct images were observed; the full set could not be checked.`,
          imageTone: 'unknown'
        };
      }
      const ids = [
        ...new Set([
          ...(container.imageIds || []),
          ...(container.imageId ? [container.imageId] : [])
        ])
      ];
      if (!ids.length || ids.some((id) => !observedDigest(id))) {
        return {
          imageStatus: 'Image not verified',
          imageDetail: `${name}/${container.name}: Kubernetes has not reported an immutable image digest.`,
          imageTone: 'unknown'
        };
      }
      const wrong = ids.filter(
        (id) =>
          normalizedRepository(id) !== repository ||
          observedDigest(id) !== expectedDigest
      );
      if (wrong.length)
        mismatches.push(
          `${name}/${container.name}: ${observedDigest(wrong[0]) ? `sha256:${observedDigest(wrong[0])!.slice(0, 12)}` : 'different repository'}`
        );
      else checked.push(`${name}/${container.name}`);
    }
  }

  if (mismatches.length)
    return {
      imageStatus: 'Different image running',
      imageDetail: mismatches.slice(0, 3).join(' · '),
      imageTone: 'failed'
    };
  return {
    imageStatus: 'Observed digest matches',
    imageDetail: `Confirmed on ${checked.join(', ')}.`,
    imageTone: 'deployed'
  };
}
